import { normaliseDateRange } from '@packages/rate-plans/utils/currentSubscription';

function buildBillingGroupFromLegacyPayload(data = {}, context = {}) {
    const investorId = context.investorId ?? data.investor_id ?? null;
    const adviserId = context.adviserId ?? data.adviser_id ?? null;

    return data.billing_group
        ? {
              ...data.billing_group,
              investor_id: investorId,
              adviser_id: adviserId
          }
        : null;
}

function buildSubscriptionsFromLegacyPayload(data = {}, context = {}) {
    const investorId = context.investorId ?? data.investor_id ?? null;
    const adviserId = context.adviserId ?? data.adviser_id ?? null;

    return (Array.isArray(data.subscriptions) ? data.subscriptions : []).map(subscription => ({
        ...subscription,
        date_range: normaliseDateRange(subscription.date_range),
        investor_id: investorId,
        adviser_id: adviserId
    }));
}

function buildRatePlansFromLegacyPayload(data = {}) {
    if (Array.isArray(data.rate_plans)) {
        return [...data.rate_plans];
    }

    if (data.rate_plans && typeof data.rate_plans === 'object') {
        return Object.values(data.rate_plans);
    }

    return [];
}

function mapBillingGroupSubscriptionRows(rows, source, { billingGroupId, investorId, adviserId, ratePlans }) {
    return (Array.isArray(rows) ? rows : []).map((subscription, index) => {
        const ratePlan = subscription?.rate_plan || null;

        if (ratePlan?.id) {
            ratePlans.push(ratePlan);
        }

        return {
            id:
                subscription?.id ||
                `${billingGroupId || 'billing-group'}-${source}-${subscription?.rate_plan_type || 'plan'}-${index}`,
            billing_group_id: billingGroupId,
            rate_plan_id: ratePlan?.id || null,
            subscription_type: subscription?.rate_plan_type || null,
            date_range: normaliseDateRange({
                start: subscription?.start_date ?? null,
                end: subscription?.end_date ?? null
            }),
            investor_id: investorId,
            adviser_id: adviserId
        };
    });
}

function normaliseBillingGroupResponse(data = {}, context = {}) {
    const investorId = context.investorId ?? data.investor_id ?? null;
    const adviserId = context.adviserId ?? data.adviser_id ?? null;
    const billingGroupId = data.id || null;

    const billing_group = billingGroupId
        ? {
              id: billingGroupId,
              name: data.name || null,
              investors_count: data.investors_count ?? null,
              investor_id: investorId,
              adviser_id: adviserId
          }
        : null;

    const ratePlans = [];
    const rowContext = { billingGroupId, investorId, adviserId, ratePlans };

    // Both active and upcoming rows go into one flat list: getCurrentSubscription /
    // getUpcomingSubscription tell them apart purely by date_range.start, which is how a
    // change scheduled for next month can be a row in each at once (see CP-5684 comment).
    const subscriptions = [
        ...mapBillingGroupSubscriptionRows(data.active_subscriptions, 'active', rowContext),
        ...mapBillingGroupSubscriptionRows(data.upcoming_subscriptions, 'upcoming', rowContext)
    ];

    return {
        billing_group,
        subscriptions,
        rate_plans: ratePlans
    };
}

/**
 * Normalise investor billing-group API payloads for ORM insertion.
 * @param {object} data
 * @param {object} [context]
 * @param {string|null} [context.investorId]
 * @param {string|null} [context.adviserId]
 * @returns {{ billing_group: object|null, subscriptions: object[], rate_plans: object[] }}
 */
export function normaliseRateSubscriptionResponse(data = {}, context = {}) {
    if (Array.isArray(data.active_subscriptions)) {
        return normaliseBillingGroupResponse(data, context);
    }

    return {
        billing_group: buildBillingGroupFromLegacyPayload(data, context),
        subscriptions: buildSubscriptionsFromLegacyPayload(data, context),
        rate_plans: buildRatePlansFromLegacyPayload(data)
    };
}

export default normaliseRateSubscriptionResponse;
